import { Injectable } from '@angular/core';
import {
  GeoJSONLineString,
  GeoJSONPoint,
  GeoJSONPolygon,
  IGeoJSON,
  IGeoJSONGeometry,
  IGeometryGeoJSON,
} from '../interfaces/geo';

import * as THREE from 'three';

@Injectable({
  providedIn: 'root',
})
export class ShapefileDataService {
  public POINT_SIZE: number = 4;
  public LINE_THICK: number = 0.5;
  public POLYGON_THICK: number = 1;
  public LINE_COLOR: number = 0x00eaff;
  public POINT_COLOR: number = 0xff0000;
  public POLYGON_COLOR: number = 0xffaa00;

  public XYToVector(coord: [number, number]): THREE.Vector2 {
    const diffx: number = coord[0];
    const diffy: number = coord[1];
    return new THREE.Vector2(diffx, diffy);
  }

  public meshToPoints(mesh: THREE.InstancedMesh): GeoJSONPoint[] {
    const json: GeoJSONPoint[] = [];
    const matrix: THREE.Matrix4 = new THREE.Matrix4();
    const position: THREE.Vector3 = new THREE.Vector3();

    for (let i: number = 0; i < mesh.count; i++) {
      mesh.getMatrixAt(i, matrix);
      matrix.decompose(position, new THREE.Quaternion(), new THREE.Vector3());
      const xy: GeoJSONPoint = [position.x, position.y];
      json.push(xy);
    }

    return json;
  }
  
  public pointsToMesh(json: IGeoJSON<IGeometryGeoJSON>[]): THREE.Mesh {
    const color: THREE.Color = new THREE.Color(this.POINT_COLOR);
    const material: THREE.MeshBasicMaterial = new THREE.MeshBasicMaterial({
      color: color,
      side: THREE.FrontSide,
    });
    const geometry: THREE.IcosahedronGeometry = new THREE.IcosahedronGeometry(
      this.POINT_SIZE,
      1
    );
    const mesh: THREE.InstancedMesh = new THREE.InstancedMesh(
      geometry,
      material,
      json.length
    );
    const matrix: THREE.Matrix4 = new THREE.Matrix4();
    for (let i: number = 0; i < mesh.count; i++) {
      const xy: GeoJSONPoint = json[i].geometry.coordinates as GeoJSONPoint;
      const v2: THREE.Vector2 = this.GetXY(xy);
      const pnt: THREE.Vector3 = new THREE.Vector3(v2.x, v2.y, 0.5);
      matrix.setPosition(pnt);
      mesh.setMatrixAt(i, matrix);
      mesh.computeBoundingBox();
    }
    return mesh;
  }
  // lines as catmull-rom curve
  public lineStringToCurve(line: GeoJSONLineString): THREE.CatmullRomCurve3 {
    const points: THREE.Vector3[] = [];
    for (const xy of line) {
      const v2: THREE.Vector2 = this.GetXY(xy);
      const pnt: THREE.Vector3 = new THREE.Vector3(v2.x, v2.y, 0.5);
      points.push(pnt);
    }
    const curve: THREE.CatmullRomCurve3 = new THREE.CatmullRomCurve3(
      points,
      false,
      'catmullrom',
      0.1
    );
    return curve;
  }
  private GetXY(coordSet: [number, number]): THREE.Vector2 {
    return this.XYToVector(coordSet);
  }

  // we do polygons in two steps...
  public polygonToShape(polygon: GeoJSONPolygon): THREE.Shape | null {
    if (polygon.length == 0 || polygon[0].length < 3) {
      console.warn('Polygon part cannot form a triangle.');
      return null;
    }

    const start: THREE.Vector2 = this.GetXY(polygon[0][0]);
    const shell: THREE.Shape = new THREE.Shape().moveTo(start.x, start.y);

    for (let i: number = 1; i < polygon[0].length; i++) {
      const coord: THREE.Vector2 = this.GetXY(polygon[0][i]);
      shell.lineTo(coord.x, coord.y);
    }

    if (polygon.length > 1) {
      for (let j: number = 1; j < polygon.length; j++) {
        if (polygon[j] == null || polygon[j].length < 3) {
          console.warn('Polygon hole cannot form a triangle.');
          continue;
        }

        const holestart: THREE.Vector2 = this.GetXY(polygon[j][0]);
        const hole: THREE.Shape = new THREE.Shape().moveTo(
          holestart.x,
          holestart.y
        );

        for (let i: number = 1; i < polygon[j].length; i++) {
          const coord: THREE.Vector2 = this.GetXY(polygon[j][i]);
          hole.lineTo(coord.x, coord.y);
        }
        shell.holes.push(hole);
      }
    }
    return shell;
  }

  public polylineShapeToMesh(jsonGeometry: IGeoJSONGeometry): THREE.Mesh {
    const color: THREE.Color = new THREE.Color(this.LINE_COLOR);
    const material: THREE.MeshBasicMaterial = new THREE.MeshBasicMaterial({
      color: color,
      side: THREE.DoubleSide,
    });
    const line: GeoJSONLineString =
      jsonGeometry.coordinates as GeoJSONLineString;
    const curve: THREE.CatmullRomCurve3 = this.lineStringToCurve(line);
    const tube: THREE.TubeGeometry = new THREE.TubeGeometry(
      curve,
      line.length,
      this.LINE_THICK,
      4,
      false
    );
    return new THREE.Mesh(tube, material);
  }

  public polygonShapeToMesh(polyShape: THREE.Shape): THREE.Mesh {
    const shpGeom: THREE.ExtrudeGeometry = new THREE.ExtrudeGeometry(
      polyShape,
      { depth: this.POLYGON_THICK }
    );
    const color: THREE.Color = new THREE.Color(this.POLYGON_COLOR);
    const material: THREE.MeshBasicMaterial = new THREE.MeshBasicMaterial({
      color: color,
      side: THREE.DoubleSide,
    });
    return new THREE.Mesh(shpGeom, material);
  }
  public studyAreaShapeToMesh(polyShape: THREE.Shape): THREE.LineSegments {
    const shpGeom: THREE.ShapeGeometry = new THREE.ShapeGeometry(polyShape);
    const geometry: THREE.EdgesGeometry = new THREE.EdgesGeometry(shpGeom); // or WireframeGeometry
    const material: THREE.LineBasicMaterial = new THREE.LineBasicMaterial({
      color: 0xffff00,
      linewidth: 2,
    });
    const edges: THREE.LineSegments = new THREE.LineSegments(
      geometry,
      material
    );
    return edges;
  }
}
